import type { StatusConversa } from '@viniko/shared';
import { X } from 'lucide-react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

export const iniciais = (nome: string) =>
  nome
    .split(/[·\s]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();

export function Avatar({
  nome,
  tamanho = 40,
  children,
}: {
  nome: string;
  tamanho?: number;
  children?: ReactNode;
}) {
  return (
    <span
      className="avatar"
      style={{ width: tamanho, height: tamanho, fontSize: tamanho * 0.36 }}
      aria-hidden
    >
      {iniciais(nome)}
      {children}
    </span>
  );
}

export const AvatarPadrao = ({ tamanho = 48 }: { tamanho?: number }) => (
  <span className="avatar-wa" style={{ width: tamanho, height: tamanho }} aria-hidden>
    <svg viewBox="0 0 48 48">
      <circle cx="24" cy="19" r="8.5" />
      <path d="M8.5 41c1.8-7.4 8.2-12 15.5-12s13.7 4.6 15.5 12A20 20 0 0 1 8.5 41Z" />
    </svg>
  </span>
);

const CORES_INICIAIS = [
  '#1e6fd9',
  '#0b8a6f',
  '#7a3fd1',
  '#c2410c',
  '#b8326e',
  '#0e7490',
  '#4d7c0f',
  '#a16207',
  '#be123c',
  '#4338ca',
];

export function iniciaisDe(nome: string): string {
  const palavras = (nome.split('·')[0] ?? '')
    .normalize('NFD')
    .replace(/[^\p{L}\s]/gu, '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!palavras.length) return '';
  const primeira = palavras[0]![0]!;
  const ultima = palavras.length > 1 ? palavras.at(-1)![0]! : '';
  return (primeira + ultima).toUpperCase();
}

function corDe(semente: string): string {
  let h = 0;
  for (const ch of semente) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return CORES_INICIAIS[h % CORES_INICIAIS.length]!;
}

export function AvatarContato({
  id,
  nome,
  tamanho = 48,
  fotoUrl,
}: {
  id: string;
  nome: string;
  tamanho?: number;
  fotoUrl?: string | null;
}) {
  const [quebrada, setQuebrada] = useState<string | null>(null);
  if (fotoUrl && quebrada !== fotoUrl) {
    return (
      <img
        className="avatar-foto"
        src={fotoUrl}
        alt=""
        width={tamanho}
        height={tamanho}
        style={{ width: tamanho, height: tamanho }}
        onError={() => setQuebrada(fotoUrl)}
      />
    );
  }
  const iniciais = iniciaisDe(nome);
  if (!iniciais) return <AvatarPadrao tamanho={tamanho} />;
  return (
    <span
      className="avatar-iniciais"
      style={
        {
          width: tamanho,
          height: tamanho,
          fontSize: tamanho * (iniciais.length > 1 ? 0.36 : 0.42),
          '--av': corDe(id),
        } as CSSProperties
      }
      aria-hidden
    >
      {iniciais}
    </span>
  );
}

export const ROTULO_STATUS: Record<StatusConversa, string> = {
  bot: 'IA respondendo',
  fila: 'Aguardando atendente',
  atendimento: 'Em atendimento',
  finalizada: 'Finalizada',
};

export const PillStatus = ({ status }: { status: StatusConversa }) => (
  <span className={`pill st-${status}`}>{ROTULO_STATUS[status]}</span>
);

export const StatusTexto = ({ status }: { status: StatusConversa }) => (
  <span className={`status-texto s-${status}`}>{ROTULO_STATUS[status]}</span>
);

export function Modal({
  titulo,
  aoFechar,
  children,
}: {
  titulo: string;
  aoFechar: () => void;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && aoFechar();
    window.addEventListener('keydown', esc);
    ref.current?.querySelector<HTMLElement>('input, textarea, select, button')?.focus();
    return () => window.removeEventListener('keydown', esc);
  }, [aoFechar]);
  return (
    <div className="modal-fundo" onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="modal" role="dialog" aria-modal aria-label={titulo} ref={ref}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 12,
          }}
        >
          <h2>{titulo}</h2>
          <button className="icone-btn sem-borda" onClick={aoFechar} aria-label="Fechar">
            <X />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

type Toast = { id: number; texto: string; tipo: 'ok' | 'erro' };
const ToastCtx = createContext<(texto: string, tipo?: Toast['tipo']) => void>(() => undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [lista, setLista] = useState<Toast[]>([]);
  const mostrar = useCallback((texto: string, tipo: Toast['tipo'] = 'ok') => {
    const id = Date.now() + Math.random();
    setLista((l) => [...l, { id, texto, tipo }]);
    setTimeout(() => setLista((l) => l.filter((t) => t.id !== id)), tipo === 'erro' ? 5000 : 2500);
  }, []);
  return (
    <ToastCtx.Provider value={mostrar}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {lista.map((t) => (
          <div key={t.id} className={`toast entra ${t.tipo === 'erro' ? 'erro' : ''}`}>
            {t.texto}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export const useToast = () => useContext(ToastCtx);

export function Vazio({
  icone,
  titulo,
  children,
}: {
  icone?: ReactNode;
  titulo: string;
  children?: ReactNode;
}) {
  return (
    <div className="vazio">
      <div>
        {icone}
        <h2>{titulo}</h2>
        {children && <p>{children}</p>}
      </div>
    </div>
  );
}
